/**
 * 发版更新提示
 *
 * 判断依据始终是版本号：拉一次 index.html，把构建时注入的 <meta name="app-build">
 * （构建时间戳，见 vite.config.ts 的 inject-build-version 插件）与当前页面里的比对，
 * 不一致即为已发新版。提示分两档：
 * - 弱提醒：右下角弹常驻通知引导用户手动刷新，可以点「稍后」。
 * - 强提醒：仅正式环境。新版本的 <meta name="app-force-update">（来自 release.config.ts）
 *   与当前页面的不同时，弹全屏遮罩，必须刷新才能继续使用。由发版人决定某次发版是否开启。
 *
 * 何时去比对分两种时机：
 * - 被动：老页面点懒加载路由会请求到已被覆盖的旧 chunk（404，表现为白屏或「点了没反应」），
 *   捕获这类加载失败后立即比对一次。它只是触发时机、不是判断依据——断网同样会加载失败，
 *   得靠版本号比对区分开。好处是用户真撞上问题的那一刻就能提示，不必等轮询周期。
 * - 主动：每 3 分钟比对一次，切回前台立即补查。测试环境检测到新版即弹弱提醒——产品 / 测试常停在
 *   同一个页面上验证，不点懒加载路由就触发不了被动信号，容易在早已修好的旧版本上复现出「bug」；
 *   正式环境的主动检测只用来发现强提醒，普通发版仍只靠被动信号弹弱提醒，不打扰坐席。
 *
 * 与 src/monitor 相互独立：监控负责把错误上报给开发，这里负责提示用户，
 * 监控总开关关闭时本提示仍需工作。
 */
import { h } from 'vue';
import { ElButton, ElMessageBox, ElNotification } from 'element-plus';
import { RefreshRight } from '@element-plus/icons-vue';
import type { NotificationHandle } from 'element-plus';
import type { Router } from 'vue-router';

/** 主动轮询间隔 */
const POLL_INTERVAL_MS = 3 * 60 * 1000;

/** 两次轮询的最小间隔：切回前台会立即补查一次，防止频繁切标签页连打请求 */
const MIN_POLL_GAP_MS = 30 * 1000;

/** 构建时注入 index.html 的两个标记名 */
const BUILD_META = 'app-build';
const FORCE_META = 'app-force-update';

/** 强提醒只在正式环境生效；测试环境发强制版本也按弱提醒处理，避免拦住测试 */
const FORCE_ENABLED = import.meta.env.MODE === 'production';

/** 路由懒加载 import 失败的错误文案，各浏览器措辞不同 */
const DYNAMIC_IMPORT_ERROR =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i;

/** 弱提醒通知实例，展示中时非空；升级为强提醒时需先关掉 */
let notice: NotificationHandle | null = null;
/** 强提醒已弹出：页面只剩刷新一条路，不再做任何检查 */
let forcing = false;
/** 版本确认请求进行中，避免同一次发版并发触发多个请求 */
let checking = false;
/** 用户点「稍后」关掉的版本标记：同一版本不再打扰，直到下一次发版 */
let dismissedBuild = '';
/** 上次轮询时间戳，配合 MIN_POLL_GAP_MS 限流 */
let lastPolledAt = 0;

/** 是否本站自己的构建产物：第三方 SDK 脚本加载失败与发版无关，不能算 */
const isOwnAsset = (url: string): boolean => {
  const { origin, pathname } = new URL(url, location.href);
  return origin === location.origin && pathname.startsWith(`${import.meta.env.BASE_URL}assets/`);
};

/** 读 index.html 中构建时注入的标记（见 vite.config.ts 的 inject-build-version 插件） */
const parseMeta = (html: string, name: string): string =>
  html.match(new RegExp(`<meta\\s+name="${name}"\\s+content="([^"]*)"`, 'i'))?.[1] ?? '';

/** 读当前页面里的标记 */
const readMeta = (name: string): string =>
  document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)?.content ?? '';

interface RemoteBuild {
  /** 线上最新的版本标记 */
  build: string;
  /** 线上最新版本是否要求当前页面强制刷新 */
  force: boolean;
}

/**
 * 拉一次 index.html 比对版本标记，确认是「发版了」而不是「网络断了」。
 * 有新版本时返回其版本标记与是否要求强制刷新；无新版本、或无从比对时返回 null。
 *
 * 有意例外（CLAUDE.md「非必要不加 try/catch」）：此处正是要区分请求成败——
 * 断网时 fetch 抛错必须吞掉并判为「非新版本」，否则会把网络故障误报成发版。
 */
const fetchNewBuild = async (): Promise<RemoteBuild | null> => {
  const current = readMeta(BUILD_META);
  /** 没有版本标记说明不是正式构建产物，无从比对 */
  if (!current) return null;
  try {
    /** 带时间戳：no-store 只能绕开浏览器缓存，query 参数才能绕开 nginx / CDN 的缓存副本 */
    const res = await fetch(`${import.meta.env.BASE_URL}index.html?_=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    const html = await res.text();
    const build = parseMeta(html, BUILD_META);
    if (!build || build === current) return null;
    /** 标记不同即要求强制刷新；跨过了某次强制版本的老页面，标记仍是旧的，同样会被拦下 */
    const forceTag = parseMeta(html, FORCE_META);
    return { build, force: FORCE_ENABLED && !!forceTag && forceTag !== readMeta(FORCE_META) };
  } catch {
    return null;
  }
};

const showNotice = (build: string): void => {
  /** 闭包内的按钮回调执行时 instance 已完成赋值 */
  const instance: NotificationHandle = ElNotification({
    title: '系统已更新到新版本',
    /** 用刷新图标指向要做的动作，替掉 type 自带的灰色感叹号 */
    icon: RefreshRight,
    /** 样式见 src/styles/element-overrides.scss 的 .app-update-notice */
    customClass: 'app-update-notice',
    position: 'bottom-right',
    /** 关闭入口只留「稍后」，右上角 × 与它重复 */
    showClose: false,
    /** 常驻不自动消失：用户可能正在操作，由他自己决定何时刷新 */
    duration: 0,
    message: h('div', [
      h('div', '为避免页面功能异常，请刷新后继续使用。'),
      h('div', { class: 'app-update-notice__actions' }, [
        h(ElButton, { size: 'small', onClick: () => instance.close() }, () => '稍后'),
        h(
          ElButton,
          { size: 'small', type: 'primary', onClick: () => location.reload() },
          () => '立即刷新',
        ),
      ]),
    ]),
    onClose: () => {
      notice = null;
      /** 记下这一版：用户已知晓，不再重复打扰，等下一次发版再提示 */
      dismissedBuild = build;
    },
  });
  notice = instance;
};

/** 强提醒：全屏遮罩，关不掉，只能刷新 */
const showForceNotice = (): void => {
  forcing = true;
  notice?.close();
  void ElMessageBox.alert(
    '系统已发布重要更新，需要刷新页面后才能继续使用。如有正在编辑的内容，请先复制保存。',
    '系统已更新到新版本',
    {
      confirmButtonText: '立即刷新',
      /** 样式见 src/styles/element-overrides.scss，层级要盖过右下角提醒 */
      modalClass: 'app-force-update-overlay',
      showClose: false,
      closeOnClickModal: false,
      closeOnPressEscape: false,
      closeOnHashChange: false,
      /** 只有点「立即刷新」会走到这里，直接刷新，不关闭弹框 */
      beforeClose: () => location.reload(),
    },
  );
};

/**
 * 查一次版本：要求强制刷新时弹强提醒；否则在允许弱提醒、且用户没关过这一版时弹弱提醒。
 * 弱提醒展示中仍会继续检查，以便同一页面后来遇到强制版本时能升级为强提醒。
 */
const checkAndNotify = async (allowWeak: boolean): Promise<void> => {
  if (forcing || checking) return;
  checking = true;
  const remote = await fetchNewBuild();
  checking = false;
  if (!remote || forcing) return;
  if (remote.force) {
    showForceNotice();
    return;
  }
  if (allowWeak && !notice && remote.build !== dismissedBuild) showNotice(remote.build);
};

/** 测试环境的主动检测会弹弱提醒；正式环境的主动检测只用来发现强提醒 */
const ALLOW_WEAK_ON_POLL = import.meta.env.MODE === 'development';

/** 一次轮询：后台标签页不查，距上次太近也不查 */
const poll = (): void => {
  if (document.hidden || Date.now() - lastPolledAt < MIN_POLL_GAP_MS) return;
  lastPolledAt = Date.now();
  void checkAndNotify(ALLOW_WEAK_ON_POLL);
};

/**
 * 注册发版更新提示。
 *
 * 本地 dev server 不启用：首次进入懒加载路由要现场编译 chunk，
 * HMR 与编译期的 404 会造成误报（同 src/monitor 的处理）。
 */
export const setupAppUpdateNotice = (router: Router): void => {
  if (__IS_DEV_SERVER__) return;

  /** 1. script / link 标签 404。资源错误不冒泡，必须在捕获阶段监听 */
  window.addEventListener(
    'error',
    (event: Event) => {
      const target = event.target;
      const url =
        target instanceof HTMLScriptElement
          ? target.src
          : target instanceof HTMLLinkElement
            ? target.href
            : '';
      if (url && isOwnAsset(url)) void checkAndNotify(true);
    },
    true,
  );

  /** 2. Vite 动态 import 预加载失败：老页面点新路由时最准的信号 */
  window.addEventListener('vite:preloadError', () => {
    void checkAndNotify(true);
  });

  /** 3. 路由懒加载 import 失败兜底 */
  router.onError((error: unknown) => {
    const message = error instanceof Error ? error.message : String(error);
    if (DYNAMIC_IMPORT_ERROR.test(message)) void checkAndNotify(true);
  });

  /** 4. 主动轮询：切回前台立即补查一次，省得后台跳过的轮询要等下一个周期 */
  setInterval(poll, POLL_INTERVAL_MS);
  document.addEventListener('visibilitychange', poll);
};
